import React, { ChangeEventHandler, KeyboardEventHandler, useState } from 'react';
import classNames from 'classnames';
import CustomError from 'Douleutaras/components/CustomError/CustomError';
import { CURRENCY } from 'Douleutaras/types';
import { KEY_DOWN, KEY_UP } from 'Douleutaras/constants/key-codes';

import './CustomInput.scss';

export enum CustomInputType {
  TEXT = 'text',
  TEXTAREA = 'textarea',
  PASSWORD = 'password',
  NUMBER = 'number'
}

export interface CustomInputProps {
  autoSelect?: boolean;
  id: string;
  // Deprecated property. Use "input.name" instead
  name?: string;
  value?: string;
  placeholder?: string;
  tabIndex?: number;
  testId?: string;
  type?: CustomInputType;
  contentOnLeft?: boolean;
  contentOnRight?: boolean;
  contentOnRightAction?: Function;
  currency?: string;
  className?: string;
  // Renders type="number" as text + inputMode="decimal" instead, and live-translates a typed
  // "," to "." — native <input type="number"> only ever accepts "." as a decimal separator, so
  // a "," (as typed throughout the el-locale UI) is otherwise silently rejected by the browser.
  translateCommaToDot?: boolean;
  input?: {
    name: string;
    value?: string,
    size?: number,
    step?: number,
    disabled?: boolean,
    maxLength?: number,
    onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
  };
  meta?: {
    touched: boolean;
    error?: string;
    warning?: string;
    note?: string;
    success?: string;
  };
  icon?: string;
  step?: string;
  // tslint:disable-next-line:no-any
  [k: string]: any;
}

export const CustomInput = (props: CustomInputProps) => {

  const {
    autoSelect,
    id,
    name: nameProp,
    placeholder,
    tabIndex,
    testId,
    contentOnLeft,
    contentOnRight,
    contentOnRightAction,
    input,
    meta,
    type = CustomInputType.TEXT,
    currency,
    icon,
    translateCommaToDot,
    ...inputProps
  } = props;

  const isDecimalCommaInput = type === CustomInputType.NUMBER && translateCommaToDot;

  const handleFocus = event => event.target.select();
  const handleChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = event => {
    event.target.value = event.target.value.trimStart();

    if (translateCommaToDot && event.target.value.includes(',')) {
      // Setting .value resets the caret to the end unless we restore it — comma and dot are both
      // a single character, so the original caret position is still valid after the swap.
      const { selectionStart, selectionEnd } = event.target;
      event.target.value = event.target.value.replace(',', '.');
      event.target.setSelectionRange(selectionStart, selectionEnd);
    }

    input?.onChange?.(event);
  };
  const handleWheel = event => event.target.blur();
  const handleKeyDown = event => {
    if (event.keyCode === KEY_UP || event.keyCode === KEY_DOWN) {
      event.preventDefault();
    }
    // Dismiss the on-screen keyboard on Enter/"next" — nothing else blurred it,
    // so mobile keypads (esp. numeric ones) stayed open over the confirm button.
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  };

  const [passwordHidden, setPasswordHidden] = useState(true);

  // props.name would be deprecated, so to keep working existing components,
  // we need to have local variable "name" which would contain the value from props.name || input.name.
  // For new components instances you need to use only input.name.
  const name = nameProp || input.name;

  return (
    <div styleName={classNames({
        'custom-input-wrapper': true,
        'custom-input-wrapper--left': contentOnLeft,
        'custom-input-wrapper--right' : contentOnRight,
        'show-error': meta?.touched && meta?.error,
        'show-success': !meta?.error && meta?.success
        })}>
      { contentOnLeft &&
        <span styleName="custom-input-left" />
      }
      <>
        { type === CustomInputType.TEXTAREA &&
          <textarea
            id={id}
            styleName="custom-input custom-input--textarea"
            name={name}
            {...input}
            {...inputProps}
            placeholder={placeholder}
            data-testid={testId}
            tabIndex={tabIndex}
            onChange={handleChange}
            onFocus={autoSelect && handleFocus}
          >
            {input.value}
          </textarea>
        }
        {/* Main Element */}
        { (type === CustomInputType.TEXT || type === CustomInputType.PASSWORD || type === CustomInputType.NUMBER) &&
            <input
              id={id}
              styleName="custom-input"
              type={type === CustomInputType.PASSWORD
                ? passwordHidden
                  ? CustomInputType.PASSWORD
                  : CustomInputType.TEXT
                : isDecimalCommaInput
                  ? CustomInputType.TEXT
                  : type
              }
              inputMode={isDecimalCommaInput ? 'decimal' : undefined}
              name={name}
              {...input}
              {...inputProps}
              placeholder={placeholder}
              data-testid={testId}
              tabIndex={tabIndex}
              enterKeyHint="done"
              onChange={handleChange}
              onFocus={autoSelect && handleFocus}
              onWheel={handleWheel}
              onKeyDown={
                typeof input.onKeyDown === 'function'
                  ? input.onKeyDown
                  : handleKeyDown
              }
            />
        }
      </>
      { contentOnRight &&
        <span styleName="custom-input-right">
          { currency &&
            <span
              className={classNames({
                dashicon: true,
                'dashicon-currency-euro': currency === CURRENCY.EUR,
                'dashicon-currency-dollar': currency === CURRENCY.USD
              })}
              styleName={classNames('custom-input__currency', {
                'custom-input__pointer': typeof contentOnRightAction === 'function'
              })}
            />
          }
          { icon &&
            <span
              className={`dashicon dashicon-${icon}`}
              styleName={classNames('custom-input__currency', {
                'custom-input__pointer': typeof contentOnRightAction === 'function'
              })}
              onClick={
                typeof contentOnRightAction === 'function'
                  ? () => contentOnRightAction()
                  : undefined
              }
            />
          }
          { type === CustomInputType.PASSWORD &&
            <span styleName="custom-input__pointer"
                  className={classNames({
                    dashicon: true,
                    'dashicon-eye-closed': passwordHidden,
                    'dashicon-eye-open': !passwordHidden
                  })}
                  onClick={() => setPasswordHidden(!passwordHidden)}
            >
            </span>
          }
        </span>
      }
      { meta?.error && <CustomError>{meta.error}</CustomError> }
      { !meta?.error && meta?.note && <CustomError type="note">{meta.note}</CustomError> }
      { !meta?.error && meta?.success && <CustomError type="success">{meta.success}</CustomError> }
    </div>
  );
};
